import type { VtoUpdate } from "./types";

type VtoUpdateCallback = (update: VtoUpdate) => void;

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function normalizeUpdate(raw: unknown, fallbackJobId?: string): VtoUpdate | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;
  const galleryId = readString(data.galleryId) || readString(data.jobId) || fallbackJobId;
  if (!galleryId) return null;
  const status = data.status === "completed" || data.status === "failed" ? data.status : "processing";
  return {
    ...data,
    galleryId,
    status,
    imageUrl: readString(data.imageUrl) || readString(data.resultImageUrl) || readString(data.outputUrl) || readString(data.remoteOutputUrl),
    error: readString(data.error) || readString(data.message),
    timestamp: typeof data.timestamp === "number" ? data.timestamp : Date.now(),
  };
}

export class SseClient {
  private eventSource: EventSource | null = null;
  private streamUrl: string;
  private listeners: Map<string, Set<VtoUpdateCallback>> = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  constructor(streamUrl: string) {
    this.streamUrl = streamUrl;
  }

  connect(): void {
    if (this.eventSource) return;

    this.eventSource = new EventSource(this.streamUrl);

    this.eventSource.addEventListener("vto-update", (event) => {
      try {
        const data = JSON.parse(event.data);
        const update = normalizeUpdate(data);
        if (update) this.emit(update.galleryId, update);
      } catch {
        // ignore malformed events
      }
    });

    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const fallbackJobId = this.listeners.keys().next().value as string | undefined;
        const update = normalizeUpdate(data, fallbackJobId);
        if (!update) return;
        this.emit(update.galleryId, update);
      } catch {
        // ignore heartbeat / malformed messages
      }
    };

    this.eventSource.onopen = () => {
      this.reconnectAttempts = 0;
    };

    this.eventSource.onerror = () => {
      this.eventSource?.close();
      this.eventSource = null;
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    if (this.listeners.size === 0) return;

    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 30000);
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  onJob(jobId: string, callback: VtoUpdateCallback): () => void {
    if (!this.listeners.has(jobId)) {
      this.listeners.set(jobId, new Set());
    }
    this.listeners.get(jobId)!.add(callback);

    // Auto-connect if not already connected
    if (!this.eventSource) {
      this.connect();
    }

    // Return unsubscribe function
    return () => {
      const jobListeners = this.listeners.get(jobId);
      if (jobListeners) {
        jobListeners.delete(callback);
        if (jobListeners.size === 0) {
          this.listeners.delete(jobId);
        }
      }
      // Auto-disconnect if no more listeners
      if (this.listeners.size === 0) {
        this.disconnect();
      }
    };
  }

  private emit(jobId: string, update: VtoUpdate): void {
    const callbacks = this.listeners.get(jobId);
    if (callbacks) {
      callbacks.forEach((cb) => cb(update));
    }
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.listeners.clear();
    this.reconnectAttempts = 0;
  }
}
